import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Eye, EyeOff } from "lucide-react";
import { validateEmail, validatePassword } from "@/lib/validations";
import { handleError } from "@/lib/errorHandling";
import { LoadingOverlay } from "@/components/LoadingOverlay";
import { ApiStatus } from "@/components/ApiStatus";
import { loginUser, ApiError, requestPasswordRecovery } from "@/services/authService";
import { useAuth } from "@/contexts/AuthContext";
import { getAppName } from "@/utils/appConfig";

interface LoginProps {
  onLogin: (email: string, viewType: "admin" | "client") => void;
}

export const Login = ({ onLogin }: LoginProps) => {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isRecoveryOpen, setIsRecoveryOpen] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [isRecoveryLoading, setIsRecoveryLoading] = useState(false);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const [recoverySuccess, setRecoverySuccess] = useState<string | null>(null);

  // Verificar se o login social está habilitado
  const isGoogleOAuthEnabled =
    import.meta.env.VITE_GOOGLE_OAUTH_ENABLED === true;

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Verificar limite de tentativas
    if (loginAttempts >= 5) {
      const errorMsg =
        "Muitas tentativas de login. Tente novamente em alguns minutos.";
      setError(errorMsg);
      return;
    }

    // Validação robusta de email
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      setError(emailValidation.message || "E-mail inválido");
      return;
    }

    // Validação robusta de senha
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.message || "Senha inválida");
      return;
    }

    setIsLoading(true);
    setShowLoadingOverlay(true);

    try {
      // Chamada real para a API de login
      const loginResponse = await loginUser({ email, password });

      // Verificar se o login foi bem-sucedido
      if (!loginResponse.success) {
        setLoginAttempts((prev) => prev + 1);
        throw new Error(
          loginResponse.error?.message || "Credenciais inválidas"
        );
      }

      // Salvar token e dados do usuário
      if (loginResponse.data?.token && loginResponse.data?.user) {
        login(loginResponse.data.token, loginResponse.data.user);
      }

      // Reset attempts on success
      setLoginAttempts(0);
      onLogin(email, "admin");
    } catch (err) {
      const errorInfo = handleError(err);
      setError(errorInfo.message);
    } finally {
      setIsLoading(false);
      setShowLoadingOverlay(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError(null);
    setIsLoading(true);
    setShowLoadingOverlay(true);

    try {
      // Simular delay de 5 segundos para Google OAuth também
      await new Promise((resolve) => setTimeout(resolve, 5000));

      // Fazer requisição para o backend para obter URL de autorização
      const response = await fetch("/api/auth/google");
      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error?.message || "Erro ao iniciar autenticação Google"
        );
      }

      // Redirecionar para o Google OAuth
      window.location.href = data.data.auth_url;
    } catch (err) {
      const errorInfo = handleError(err);
      setError(errorInfo.message);
      setIsLoading(false);
      setShowLoadingOverlay(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <img
            src="/logo.svg"
            alt="Logo"
            className="mx-auto"
            style={{ width: "250px" }}
          />
          <h1 className="text-3xl font-bold bg-gradient-primary bg-clip-text text-transparent">
            {getAppName()}
          </h1>
          <p className="text-muted-foreground">
            Dashboard de Monitoramento de API
          </p>
        </div>

        <Card className="shadow-2xl border-0 bg-card/50 backdrop-blur-sm">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-semibold">
              Fazer Login
            </CardTitle>
            <CardDescription>
              Entre com sua conta para acessar o dashboard
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive" className="text-sm">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Botão do Google - só aparece se OAuth estiver habilitado */}
            {isGoogleOAuthEnabled && (
              <>
                <Button
                  onClick={handleGoogleLogin}
                  variant="outline"
                  className="w-full h-12 border-2 hover:border-primary/50 hover:bg-primary/5"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="animate-spin mr-2">&#9696;</span>
                  ) : (
                    <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                  )}
                  Continuar com Google
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <Separator className="w-full" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">
                      ou
                    </span>
                  </div>
                </div>
              </>
            )}

            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="seu@mail.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12"
                  disabled={isLoading}
                  autoComplete="username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                    className="h-12 pr-10"
                    disabled={isLoading}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
                {password && (
                  <div className="text-xs text-muted-foreground">
                    Força da senha:{" "}
                    {validatePassword(password).strength === "strong"
                      ? "🟢 Forte"
                      : validatePassword(password).strength === "medium"
                      ? "🟡 Média"
                      : "🔴 Fraca"}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between">
                <div />
                <button
                  type="button"
                  className="text-sm text-primary hover:underline"
                  onClick={() => {
                    setRecoveryEmail(email || "");
                    setRecoveryError(null);
                    setRecoverySuccess(null);
                    setIsRecoveryOpen(true);
                  }}
                  disabled={isLoading}
                >
                  Esqueci minha senha
                </button>
              </div>
              <Button
                type="submit"
                className="w-full h-12 bg-gradient-primary hover:opacity-90 shadow-glow"
                disabled={isLoading}
              >
                {isLoading ? "Processando..." : "Entrar"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Status da API */}
        <div className="flex justify-center">
          <ApiStatus />
        </div>
      </div>

      {/* Dialog Recuperação de Senha */}
      <Dialog open={isRecoveryOpen} onOpenChange={setIsRecoveryOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Recuperar senha</DialogTitle>
            <DialogDescription>
              Informe o e-mail cadastrado. Se existir em nossa base, enviaremos um link de recuperação válido por 24 horas.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {recoveryError && (
              <Alert variant="destructive" className="text-sm">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{recoveryError}</AlertDescription>
              </Alert>
            )}
            {recoverySuccess && (
              <Alert className="text-sm">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{recoverySuccess}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="recovery-email">E-mail</Label>
              <Input
                id="recovery-email"
                type="email"
                placeholder="seu@mail.com"
                value={recoveryEmail}
                onChange={(e) => setRecoveryEmail(e.target.value)}
                autoFocus
                disabled={isRecoveryLoading}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={isRecoveryLoading}>
                Cancelar
              </Button>
            </DialogClose>
            <Button
              type="button"
              onClick={async () => {
                setRecoveryError(null);
                setRecoverySuccess(null);
                const validation = validateEmail(recoveryEmail);
                if (!validation.isValid) {
                  setRecoveryError(validation.message || "E-mail inválido");
                  return;
                }
                try {
                  setIsRecoveryLoading(true);
                  const resp = await requestPasswordRecovery(recoveryEmail.trim());
                  if (resp.success) {
                    setRecoverySuccess(
                      resp.message ||
                        "Se o e-mail existir, um link de recuperação foi enviado. Verifique sua caixa de entrada e o spam."
                    );
                  } else {
                    setRecoveryError(
                      resp.error?.message ||
                        "Não foi possível enviar o e-mail de recuperação. Tente novamente mais tarde."
                    );
                  }
                } catch (err) {
                  const info = handleError(err as ApiError);
                  setRecoveryError(info.message);
                } finally {
                  setIsRecoveryLoading(false);
                }
              }}
              disabled={isRecoveryLoading}
            >
              {isRecoveryLoading ? "Enviando..." : "Enviar link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loading Overlay */}
      <LoadingOverlay
        isVisible={showLoadingOverlay}
        message="Fazendo login..."
      />
    </div>
  );
};
